import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type Brand } from "@/api/client";
import type { Store } from "@/api/types";
import { applyBrandTheme } from "@/brand/BrandProvider";

type Ctx = { stores: (Store & { brand: Brand })[]; store: (Store & { brand: Brand }) | null; setStoreId: (id: string) => void; loading: boolean };
const StoreCtx = createContext<Ctx | null>(null);
const KEY = "studio.storeId";

export function StoreProvider({ children }: { children: ReactNode }) {
  const brands = useQuery({ queryKey: ["brands"], queryFn: () => api<Brand[]>("/app/brands") });
  const storesQ = useQuery({ queryKey: ["stores"], queryFn: () => api<Store[]>("/app/stores") });
  const [storeId, setStoreIdState] = useState<string | null>(() => { try { return localStorage.getItem(KEY); } catch { return null; } });
  const stores = useMemo(() => {
    const byBrand = new Map((brands.data ?? []).map((b) => [b.id, b]));
    return (storesQ.data ?? []).flatMap((s) => (byBrand.get(s.brand_id) ? [{ ...s, brand: byBrand.get(s.brand_id)! }] : []));
  }, [brands.data, storesQ.data]);
  useEffect(() => { if (!storeId && stores.length) setStoreIdState(stores[0].id); }, [stores, storeId]);
  const setStoreId = (id: string) => { setStoreIdState(id); try { localStorage.setItem(KEY, id); } catch { /* ignore */ } };
  const store = stores.find((s) => s.id === storeId) ?? null;
  // the admin shell wears the colours of the selected store's brand
  useEffect(() => { if (store) applyBrandTheme(store.brand.slug, store.brand.theme); }, [store?.brand.slug, store?.brand.theme]); // eslint-disable-line react-hooks/exhaustive-deps
  return <StoreCtx.Provider value={{ stores, store, setStoreId, loading: brands.isLoading || storesQ.isLoading }}>{children}</StoreCtx.Provider>;
}

export function useStore() {
  const v = useContext(StoreCtx);
  if (!v) throw new Error("useStore outside StoreProvider");
  return v;
}
